import { useQuery } from "@tanstack/react-query";

import { RbacApi, type AppUser } from "./api";

/**
 * Returns a stable lookup of UID → AppUser, plus a `name(uid)` helper that
 * resolves to the user's display name (falling back to email, then UID
 * if the user record isn't visible to the caller).
 *
 * Backed by the same `["users"]` query the Users page uses, so it's a free
 * re-read once that's been fetched.
 */
export function useUsersById(): {
  byId: Map<string, AppUser>;
  name: (uid: string | null | undefined) => string;
} {
  const users = useQuery({ queryKey: ["users"], queryFn: RbacApi.listUsers });
  const byId = new Map<string, AppUser>();
  for (const u of users.data?.items ?? []) byId.set(u.id, u);
  return {
    byId,
    name: (uid) => {
      if (!uid) return "—";
      const u = byId.get(uid);
      if (!u) return uid; // not in scope or not yet loaded
      return u.fullName || u.email || uid;
    },
  };
}
